<template>
    <div class="fx-rate-lock-group">
        <div
            class="fx-rate-lock-badge"
            :class="{
                'fx-rate-lock-badge--normal': urgencyState === 'normal',
                'fx-rate-lock-badge--warning': urgencyState === 'warning',
                'fx-rate-lock-badge--danger': urgencyState === 'danger',
                'fx-rate-lock-badge--expired': urgencyState === 'expired',
            }"
            data-cy="fx-rate-lock-countdown"
        >
            <v-icon
                :icon="
                    urgencyState === 'expired'
                        ? mdiAlertCircleOutline
                        : mdiClockOutline
                "
                size="16"
            />
            <strong v-if="urgencyState !== 'expired'">
                {{ t("organisationHeader.rateLockedLabel") }}
                {{ timeRemaining }}
            </strong>
            <strong v-else>{{
                t("organisationHeader.quoteExpiredLabel")
            }}</strong>
        </div>

        <div v-if="urgencyState === 'expired'">
            <p class="fx-rate-info--expired">
                {{ t("organisationHeader.exchangeRateExpired") }}
            </p>
        </div>

        <core-button
            v-if="urgencyState === 'expired'"
            data-cy="refresh-quote-button"
            colour="primary"
            class="refresh-quote-button sm-button"
            :aria-label="t('organisationHeader.refreshQuoteCTA')"
            :prepend-icon="mdiRefresh"
            :is-loading="isLoadingPaymentMethods"
            @click="fetchPaymentMethodsAndRedirect"
        >
            {{ t("organisationHeader.refreshQuoteCTA") }}
        </core-button>
    </div>
</template>

<script setup lang="ts">
import { computed, ref, watch, onMounted, onUnmounted } from "vue";
import { useI18n } from "vue-i18n";
import { mdiClockOutline, mdiAlertCircleOutline, mdiRefresh } from "@mdi/js";
import { CoreButton } from "@esenda/core-ui";
import { useFetchPaymentMethodsAndRedirect } from "@/composables/useFetchPaymentMethodsAndRedirect";
import {
    getQuoteUrgencyState,
    formatCountdown,
} from "@/helpers/dateProcessors";

const props = defineProps<{
    quoteExpiryDate?: string | null;
}>();

const emit = defineEmits<{
    expired: [];
}>();

const { t } = useI18n();

const { isLoadingPaymentMethods, fetchPaymentMethodsAndRedirect } =
    useFetchPaymentMethodsAndRedirect();

const now = ref(Date.now());
let timerInterval: ReturnType<typeof setInterval> | null = null;

onMounted(() => {
    timerInterval = setInterval(() => {
        now.value = Date.now();
    }, 1000);
});

onUnmounted(() => {
    if (timerInterval) clearInterval(timerInterval);
});

const urgencyState = computed(
    (): "normal" | "warning" | "danger" | "expired" | null => {
        if (!props.quoteExpiryDate) return null;
        return getQuoteUrgencyState(props.quoteExpiryDate, now.value);
    },
);

watch(urgencyState, (state) => {
    if (state === "expired") emit("expired");
});

const timeRemaining = computed(() => {
    if (!props.quoteExpiryDate || urgencyState.value === "expired") return null;

    const formattedDate = props.quoteExpiryDate.split(".")[0] + "Z";
    const expiryTime = new Date(formattedDate).getTime();
    const diffMs = Math.max(expiryTime - now.value, 0);

    return formatCountdown(diffMs);
});
</script>

<style scoped>
.fx-rate-lock-group {
    display: inline-flex;
    flex-direction: column;
    align-items: stretch;
    gap: 4px;
    margin-top: 4px;
}

.fx-rate-lock-badge {
    display: inline-flex;
    align-items: center;
    justify-content: center;
    gap: 6px;
    padding: 6px 14px;
    border-radius: 999px; /** TODO update this */
    font-size: 13px;
    transition:
        background-color 0.4s ease,
        color 0.4s ease;
}

.fx-rate-lock-badge--normal {
    background-color: var(--badge-success-bg);
    color: var(--badge-success-text);
}

.fx-rate-lock-badge--warning {
    background-color: var(--badge-warning-bg);
    color: var(--badge-warning-text);
}

.fx-rate-lock-badge--danger {
    background-color: var(--badge-danger-bg);
    color: var(--badge-danger-text);
    animation: rate-lock-pulse 1.5s ease-in-out infinite;
}

.fx-rate-lock-badge--expired {
    background-color: var(--surface-neutral);
    color: var(--text-label-strong);
}

.fx-rate-info--expired {
    font-size: 13px;
    color: var(--text-secondary);
}

@keyframes rate-lock-pulse {
    0%,
    100% {
        opacity: 1;
    }
    50% {
        opacity: 0.6;
    }
}
</style>
